// src/components/dashboard/OrderDetail.tsx
import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { useAuth } from '../../contexts/AuthContext'
import { apiService, OrderDetailResponse } from '../../services/api'
import { formatCurrency } from '../../utils/format'
import toast from 'react-hot-toast'
import { ArrowLeft, Package, Clock, Info, CheckCircle, Truck, XCircle, DollarSign, Undo2, ExternalLink, Hash, Calendar, UserCircle } from 'lucide-react'

interface OrderDetailProps {
  order: ScrapeOrder;
  onBack: () => void;
}

interface ScrapeOrder {
  id: string;
  orderId: string;
  orderDateUtc: string | null;
  total: number | null;
  status: string | null;
  itemCount: number;
  createdAtUtc: string;
  updatedAtUtc: string;
  items?: ScrapeOrderItem[];
  buyerEmail?: string | null;
}

interface ScrapeOrderItem {
  title: string;
  asin: string | null;
  link: string | null;
  image: string | null;
  price: number | null;
  quantity: number | null;
  category: string;
  /** Amazon'un ödediği TAM oran (%) — sunucudan gelir */
  commissionRate?: number;
  /** Kullanıcı payı oranı (%) — sunucuda hesaplanır */
  userRate?: number;
  /** Kullanıcı payı tutarı — sunucuda hesaplanır */
  userEarnings?: number;
}


// Amazon sipariş URL'i oluştur
const getAmazonOrderUrl = (orderId: string): string => {
  return `https://www.amazon.com/gp/your-account/order-details/?orderID=${orderId}`
}




// Komisyon SUNUCUDA hesaplanır — bu ekranlar yalnızca gösterir.
// Oran tablosu ve kullanıcı payı kuralı tek yerde yaşar:
//   AmazonBonus.Api/Services/CommissionCalculator.cs
// API her kalem için hazır `userRate` (%) ve `userEarnings` (tutar) gönderir;
// eski yanıtlar için `commissionRate / 2` yedeği bırakıldı.
const getCommissionRate = (item: ScrapeOrderItem): number => {
  if (item.userRate !== undefined && item.userRate !== null) return item.userRate;
  if (item.commissionRate !== undefined && item.commissionRate !== null) return item.commissionRate / 2;
  return 0;
};


const translateStatus = (status: string | null): string => {
  if (!status) return 'Beklemede'
  
  const statusLower = status.toLowerCase()
  

  if (statusLower.includes('arriving')) {
    return `Kargoda - ${status}`
  }

  if (statusLower.includes('delivered')) return 'Teslim Edildi'
  

  switch (statusLower) {
    case 'cancelled':
      return 'İptal Edildi'
    case 'refunded':
      return 'Para İadesi Yapıldı'
    case 'return started':
      return 'İade Talebi Mevcut'
    case 'test':
      return 'Test'
    case 'processing':
      return 'İşleniyor'
    case 'pending':
      return 'Beklemede'
    default:
      return status
  }
}


const normalizeTurkish = (text: string): string => {
  return text
    .replace(/İ/g, 'i')
    .replace(/I/g, 'i')  
    .replace(/Ğ/g, 'g')
    .replace(/Ü/g, 'u')
    .replace(/Ş/g, 's')
    .replace(/Ö/g, 'o')
    .replace(/Ç/g, 'c')
    .toLowerCase()  // En son toLowerCase yap
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/ı/g, 'i')
}

// Timeline icon component
const TimelineIcon = ({ status }: { status: string }) => {
  const statusNormalized = normalizeTurkish(status)
  
  if (statusNormalized.includes('siparis olusturuldu') || statusNormalized.includes('order created')) {
    return (
      <div className="absolute left-[-2px] top-[-2px] w-7 h-7 rounded-full bg-orange-50 border-2 border-orange-300 grid place-items-center text-orange-500 font-extrabold text-sm">
        <Package className="w-3.5 h-3.5" />
      </div>
    )
  }
  
  if (statusNormalized.includes('beklemede') || statusNormalized.includes('pending')) {
    return (
      <div className="absolute left-[-2px] top-[-2px] w-7 h-7 rounded-full bg-yellow-50 border-2 border-yellow-300 grid place-items-center text-yellow-600 font-extrabold text-sm">
        <Clock className="w-3.5 h-3.5" />
      </div>
    )
  }
  
  if (statusNormalized.includes('kargoda') || statusNormalized.includes('arriving')) {
    return (
      <div className="absolute left-[-2px] top-[-2px] w-7 h-7 rounded-full bg-blue-50 border-2 border-blue-300 grid place-items-center text-blue-600 font-extrabold text-sm">
        <Truck className="w-3.5 h-3.5" />
      </div>
    )
  }
  
  if (statusNormalized.includes('teslim edildi') || statusNormalized.includes('delivered')) {
    return (
      <div className="absolute left-[-2px] top-[-2px] w-7 h-7 rounded-full bg-green-50 border-2 border-green-300 grid place-items-center text-green-600 font-extrabold text-sm">
        <CheckCircle className="w-3.5 h-3.5" />
      </div>
    )
  }
  
  if (statusNormalized.includes('iptal') || statusNormalized.includes('cancelled')) {
    return (
      <div className="absolute left-[-2px] top-[-2px] w-7 h-7 rounded-full bg-red-50 border-2 border-red-300 grid place-items-center text-red-600 font-extrabold text-sm">
        <XCircle className="w-3.5 h-3.5" />
      </div>
    )
  }
  
  if (statusNormalized.includes('iade') || statusNormalized.includes('refund') || statusNormalized.includes('return')) {
    return (
      <div className="absolute left-[-2px] top-[-2px] w-7 h-7 rounded-full bg-purple-50 border-2 border-purple-300 grid place-items-center text-purple-600 font-extrabold text-sm">
        <Undo2 className="w-3.5 h-3.5" />
      </div>
    )
  }
  
  // Default
  return (
    <div className="absolute left-[-2px] top-[-2px] w-7 h-7 rounded-full bg-gray-50 border-2 border-gray-300 grid place-items-center text-gray-600 font-extrabold text-sm">
      <Clock className="w-3.5 h-3.5" />
    </div>
  )
}

export default function OrderDetail({ order, onBack }: OrderDetailProps) {
  const { user } = useAuth()
  const [loading] = useState(false)

const formatPrice = (price: number | null) => {
  return formatCurrency(price)
}


const calculateTotal = () => {
  // order.total zaten doğru toplam tutarı içeriyor
  if (order.total != null) {
    return order.total
  }
  
  // Fallback: items'dan hesapla ama price zaten toplam olduğu için quantity ile çarpmıyoruz
  if (!order.items) return 0
  return order.items.reduce((sum, item) => {
    return sum + (item.price || 0)  // ✅ price zaten (birim × miktar)
  }, 0)
}

const calculateItemCommission = (item: ScrapeOrderItem) => {
  // Sunucu hazır tutarı gönderiyor; yoksa orandan hesapla (eski yanıt yedeği)
  if (item.userEarnings !== undefined && item.userEarnings !== null) return item.userEarnings;
  return ((item.price || 0) * getCommissionRate(item)) / 100;
}

const calculateTotalCommission = () => {
  if (!order || !order.items) return 0
  return order.items.reduce((sum, item) => sum + calculateItemCommission(item), 0)
}

  // Timeline events oluştur
  const getTimelineEvents = () => {
    const events = []
    
    // 1. Sipariş Oluşturuldu (her zaman var)
    events.push({
      title: 'Sipariş Oluşturuldu',
      date: order.createdAtUtc
    })
    
    // 2. Beklemede (her zaman var)
    events.push({
      title: 'Sipariş durumu "Beklemede" olarak değiştirildi',
      date: order.createdAtUtc
    })
    
    // 3. Eğer status var ve beklemede değilse, gerçek statusu ekle
    if (order.status && order.status.toLowerCase() !== 'pending' && order.status.toLowerCase() !== 'beklemede') {
      const translatedStatus = translateStatus(order.status)
      events.push({
        title: `Sipariş durumu "${translatedStatus}" olarak değiştirildi`,
        date: order.updatedAtUtc
      })
    }
    
    return events
  }

  const timelineEvents = getTimelineEvents()

  // Durum → renk (Amazon tarzı ilerleme çubuğu). Tailwind purge'e takılmasın diye inline hex.
  const STATUS_HEX: Record<string, string> = {
    created: '#fb923c',   // turuncu — oluşturuldu
    pending: '#facc15',   // sarı — beklemede
    shipping: '#60a5fa',  // mavi — kargoda
    delivered: '#22c55e', // yeşil — teslim
    cancelled: '#f87171', // kırmızı — iptal/iade
    default: '#9ca3af',   // gri
  }
  const statusKey = (title: string) => {
    const t = normalizeTurkish(title || '')
    if (t.includes('iptal') || t.includes('cancel') || t.includes('iade') || t.includes('refund') || t.includes('return')) return 'cancelled'
    if (t.includes('teslim') || t.includes('delivered')) return 'delivered'
    if (t.includes('kargoda') || t.includes('arriving')) return 'shipping'
    if (t.includes('beklemede') || t.includes('pending')) return 'pending'
    if (t.includes('olusturuldu') || t.includes('created')) return 'created'
    return 'default'
  }
  const dotHex = (title: string) => STATUS_HEX[statusKey(title)] || STATUS_HEX.default
  // Son olay = mevcut/aktif durum. Çizgi son (aktif) noktada biter.
  const currentIndex = timelineEvents.length - 1

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-3">
          <div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-600">Sipariş detayları yükleniyor...</span>
        </div>
      </div>
    )
  }

  if (!order || !order.items || order.items.length === 0) {
    return (
      <div className="text-center py-12">
        <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <p className="text-gray-600 text-lg">Sipariş detayları bulunamadı</p>
        <Button 
          onClick={onBack}
          variant="outline"
          className="mt-4 border-orange-200 hover:bg-orange-50"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Geri Dön
        </Button>
      </div>
    )
  }

  return (
    <div className="max-w-[1080px] mx-auto px-6 py-7">
      {/* Breadcrumb */}
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }}
        className="flex items-center gap-2 text-sm text-gray-700 mb-5"
      >
        <button onClick={onBack} className="hover:text-orange-600 transition-colors">
          Kontrol Paneli
        </button>
        <span className="text-gray-400">›</span>
        <button onClick={onBack} className="hover:text-orange-600 transition-colors">
          Siparişler
        </button>
        <span className="text-gray-400">›</span>
        <span className="font-semibold text-gray-900">Sipariş Detayları</span>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_0.95fr] gap-5">
        {/* Left Column - Order Summary */}
        <motion.div 
          initial={{ opacity: 0, x: -20 }} 
          animate={{ opacity: 1, x: 0 }}
        >
          <Card className="border border-gray-200 shadow-[0_10px_30px_rgba(2,8,20,0.06)] rounded-[14px] overflow-hidden">
            <CardHeader className="p-0 m-0 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-transparent">
              <CardTitle className="text-sm font-extrabold tracking-wide text-orange-600 px-[18px] py-4 m-0">
                SİPARİŞ ÖZETİ
              </CardTitle>
            </CardHeader>
<CardContent className="px-[18px] py-4 pb-2">
  {/* Sipariş No ve Tarih */}
  <div className="space-y-3 mb-4">
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center">
        <Hash className="w-4 h-4 text-blue-600" />
      </div>
      <div>
        <p className="text-xs text-gray-500">Sipariş No</p>
        <button
          onClick={() => window.open(getAmazonOrderUrl(order.orderId), '_blank', 'noopener,noreferrer')}
          className="text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 transition-colors"
          title="Amazon'da aç"
        >
          {order.orderId}
          <ExternalLink className="w-3 h-3" />
        </button>
      </div>
    </div>

    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-purple-50 flex items-center justify-center">
        <Calendar className="w-4 h-4 text-purple-600" />
      </div>
      <div>
        <p className="text-xs text-gray-500">Sipariş Tarihi</p>
        <p className="text-sm font-medium text-gray-800">
          {new Date(order.createdAtUtc).toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: '2-digit', 
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </p>
      </div>
    </div>

    {/* Alıcı Hesabı — sipariş hangi Amazon hesabından verildi.
        Birden fazla alıcı hesabı kullananlar için: durumu güncellemek üzere hangi
        hesaba girileceğini söyler. Boşsa eklenti henüz okuyamamış demektir. */}
    <div className="flex items-center gap-3">
      <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
        <UserCircle className="w-4 h-4 text-amber-600" />
      </div>
      <div className="min-w-0">
        <p className="text-xs text-gray-500">Alıcı Hesabı</p>
        {order.buyerEmail ? (
          <p className="text-sm font-medium text-gray-800 truncate" title={order.buyerEmail}>
            {order.buyerEmail}
          </p>
        ) : (
          <p className="text-sm text-gray-400 italic">Henüz okunmadı — bir sonraki güncellemede dolar</p>
        )}
      </div>
    </div>
  </div>

  {/* Tutar Kartları */}
  <div className="grid grid-cols-2 gap-3 mb-4">
    <div className="p-3 rounded-xl bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200">
      <div className="flex items-center gap-2 mb-1">
        <DollarSign className="w-4 h-4 text-green-600" />
        <span className="text-xs text-green-700">Sipariş Tutarı</span>
      </div>
      <p className="text-lg font-bold text-green-700">{formatPrice(calculateTotal())}</p>
    </div>

    <div className="p-3 rounded-xl bg-gradient-to-br from-red-50 to-orange-50 border border-red-200">
      <div className="flex items-center gap-2 mb-1">
        <Undo2 className="w-4 h-4 text-red-600" />
        <span className="text-xs text-red-700">Para İadesi</span>
      </div>
      <p className="text-lg font-bold text-red-700">
        {(order.status?.toLowerCase() === 'refunded' || 
          order.status?.toLowerCase() === 'para iadesi yapıldı') 
          ? formatPrice(calculateTotal()) 
          : '$0.00'}
      </p>
    </div>
  </div>

  {/* Divider */}
  <div className="h-px bg-gray-200 my-3"></div>

{/* Estimated Commission */}
<div className="flex items-center gap-2 font-semibold text-sm">
  <div className="w-[18px] h-[18px] border border-gray-400 rounded-full grid place-items-center text-xs text-gray-600">
    <Info className="w-3 h-3" />
  </div>
  <span className="text-gray-700">Tahmini Komisyon Tutarı:</span>
  {(order.status?.toLowerCase() === 'cancelled' || order.status?.toLowerCase() === 'refunded') ? (
    <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-md bg-red-50 text-red-600 border border-red-200 line-through decoration-2">
      {formatPrice(calculateTotalCommission())}
    </span>
  ) : order.status?.toLowerCase() === 'return started' ? (
    <span className="ml-auto inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-yellow-50 text-yellow-600 border border-yellow-200">
      {formatPrice(calculateTotalCommission())}
      <Clock className="w-3 h-3" />
    </span>
  ) : order.status?.toLowerCase().includes('delivered') ? (
    <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-md bg-green-50 text-green-600 border border-green-200">
      {formatPrice(calculateTotalCommission())}
    </span>
  ) : order.status?.toLowerCase().includes('arriving') ? (
    <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-md bg-blue-50 text-blue-600 border border-blue-200">
      {formatPrice(calculateTotalCommission())}
    </span>
  ) : (
    <span className="ml-auto inline-flex items-center px-2 py-0.5 rounded-md bg-orange-50 text-orange-600 border border-orange-200">
      {formatPrice(calculateTotalCommission())}
    </span>
  )}
</div>

              {/* Items Section */}
              <div className="mt-2">
                {order.items && order.items.map((item, index) => {
                  return (
                    <div key={index} className="py-3.5 border-t border-dashed border-gray-200 first:border-t-0">
                      {/* Product Row */}
                      <div className="grid grid-cols-[72px_1fr_80px] gap-3 items-center">
                        {/* Thumbnail */}
                        <div className="w-[72px] h-[54px] rounded-lg border border-gray-200 bg-white overflow-hidden">
                          {item.image ? (
                            <img 
                              src={item.image} 
                              alt={item.title}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-gray-100">
                              <Package className="w-6 h-6 text-gray-400" />
                            </div>
                          )}
                        </div>

                        {/* Title & Meta */}
                        <div>
                          <div className="text-[13.5px] leading-[1.35] text-gray-900 mb-1">
                            <a href={item.link || '#'} target="_blank" rel="noopener noreferrer" className="text-gray-900 hover:text-orange-600">
                              {item.title}
                            </a>
                          </div>
                        </div>

                        {/* Quantity */}
                        <div className="justify-self-end text-gray-600 text-[13px]">
                          {item.quantity}
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </CardContent>
          </Card>
        </motion.div>

        {/* Right Column - Status */}
        <motion.div 
          initial={{ opacity: 0, x: 20 }} 
          animate={{ opacity: 1, x: 0 }}
        >
          <Card className="border border-gray-200 shadow-[0_10px_30px_rgba(2,8,20,0.06)] rounded-[14px] overflow-hidden">
            <CardHeader className="p-0 m-0 border-b border-gray-200 bg-gradient-to-r from-orange-50 to-transparent">
              <CardTitle className="text-sm font-extrabold tracking-wide text-orange-600 px-[18px] py-4 m-0">
                SİPARİŞ DURUMU
              </CardTitle>
            </CardHeader>
            <CardContent className="px-[18px] py-3 pb-1">
              {/* Note */}
              <p className="text-sm leading-[1.55] text-gray-600 my-1.5 mb-4">
                Komisyon, sadece teslim edilen siparişler için geçerlidir. İade, iptal veya 
                para iadesi taleplerinde komisyon kaybedilebilir. Tüm ürünler teslim 
                edildiğinde komisyon alırsınız.
              </p>

              {/* Timeline */}
              <div className="relative ml-1.5 pl-7">
                {/* Timeline Items */}
                {timelineEvents.map((event, index) => {
                  
                  
                  // Balon rengini belirle
                  const getBalloonStyle = () => {
                    // Türkçe karakterleri normalize et
                    const titleNormalized = normalizeTurkish(event.title)
                    
                    
                    // İptal durumları - KIRMIZI
                    if (titleNormalized.includes('iptal') || 
                        titleNormalized.includes('cancelled') ||
                        titleNormalized.includes('cancel')) {
                      
                      return 'bg-red-50 border-red-300'
                    }
                    
                    // İade durumları - KIRMIZI
                    if (titleNormalized.includes('iade') || 
                        titleNormalized.includes('refund') || 
                        titleNormalized.includes('return') ||
                        titleNormalized.includes('para iadesi')) {
                      
                      return 'bg-red-50 border-red-300'
                    }
                    
                    // Teslim edildi - YEŞİL
                    if (titleNormalized.includes('teslim') || titleNormalized.includes('delivered')) {
                      
                      return 'bg-green-50 border-green-300'
                    }
                    
                    // Kargoda - MAVİ
                    if (titleNormalized.includes('kargoda') || titleNormalized.includes('arriving')) {
                      
                      return 'bg-blue-50 border-blue-300'
                    }
                    
                    // Beklemede - SARI
                    if (titleNormalized.includes('beklemede') || titleNormalized.includes('pending')) {
                      
                      return 'bg-yellow-50 border-yellow-300'
                    }
                    
                    // Sipariş oluşturuldu - TURUNCU
                    if (titleNormalized.includes('olusturuldu') || titleNormalized.includes('created')) {
                     
                      return 'bg-orange-50 border-orange-300'
                    }
                    
                    // Default - GRİ

                    return 'bg-gray-50 border-gray-300'
                  }

                  const isCurrent = index === currentIndex
                  const nodeHex = dotHex(event.title)

                  return (
                    <div key={index} className="relative my-3.5">
                      {/* Bu adımdan sonrakine uzanan renkli bağlayıcı — SON adımda yok → çizgi burada biter */}
                      {index < currentIndex && (
                        <span
                          className="absolute left-[-14px] w-1 rounded-full"
                          style={{
                            top: '12px',
                            bottom: '-26px',
                            background: `linear-gradient(to bottom, ${nodeHex}, ${dotHex(timelineEvents[index + 1].title)})`
                          }}
                        ></span>
                      )}
                      {/* Çizgi üzerindeki ilerleme noktası — aktif (mevcut) durum büyük + canlı halka */}
                      {isCurrent && (
                        <span
                          className="absolute left-[-21px] top-0 w-[18px] h-[18px] rounded-full animate-ping"
                          style={{ backgroundColor: nodeHex, opacity: 0.35 }}
                        ></span>
                      )}
                      <span
                        className={`absolute rounded-full ring-2 ring-white z-10 ${isCurrent ? 'left-[-20px] top-0.5 w-4 h-4' : 'left-[-18px] top-1.5 w-3 h-3'}`}
                        style={{
                          backgroundColor: nodeHex,
                          boxShadow: isCurrent ? `0 0 0 4px ${nodeHex}33` : '0 1px 2px rgba(0,0,0,0.15)'
                        }}
                      ></span>
                      <TimelineIcon status={event.title} />
                      <div className={`border rounded-[10px] p-2.5 px-3 ml-3 ${getBalloonStyle()}`}>
                        <div className="text-sm font-semibold mb-1">{event.title}</div>
                        <div className="text-xs text-gray-600">
                          {event.date 
                            ? new Date(event.date).toLocaleDateString('tr-TR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              }) + ' ' + 
                              new Date(event.date).toLocaleTimeString('tr-TR', { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })
                            : new Date().toLocaleDateString('tr-TR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric'
                              }) + ' ' + 
                              new Date().toLocaleTimeString('tr-TR', { 
                                hour: '2-digit', 
                                minute: '2-digit' 
                              })
                          }
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>

              {/* Timestamp */}
              <div className="flex items-center gap-2 text-gray-400 text-xs mt-4 mb-2">
                <Clock className="w-4 h-4" />
                <span>
                  {order.updatedAtUtc 
                    ? new Date(order.updatedAtUtc).toLocaleDateString('tr-TR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      }) + ' – ' + 
                      new Date(order.updatedAtUtc).toLocaleTimeString('tr-TR', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })
                    : new Date().toLocaleDateString('tr-TR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      }) + ' – ' + 
                      new Date().toLocaleTimeString('tr-TR', { 
                        hour: '2-digit', 
                        minute: '2-digit' 
                      })
                  }
                </span>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  )
}